// Phase 2: Entity Selector Component
// Searchable combobox for selecting releases/artists/clients/campaigns

import React, { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import type { ReportFocus } from '../../../shared/types/reports';

interface EntityOption {
  id: string;
  name: string;
  subtitle?: string; // e.g., "Artist Name" for releases, "Company Name" for clients
  metadata?: Record<string, any>;
}

interface EntitySelectorProps {
  focus: ReportFocus;
  options: EntityOption[];
  selected: EntityOption | null;
  onChange: (entity: EntityOption | null) => void;
  loading?: boolean;
  placeholder?: string;
}

export const EntitySelector: React.FC<EntitySelectorProps> = ({
  focus,
  options,
  selected,
  onChange,
  loading = false,
  placeholder,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    
    const term = searchTerm.toLowerCase();
    return options.filter(
      (opt) =>
        opt.name.toLowerCase().includes(term) ||
        opt.subtitle?.toLowerCase().includes(term)
    );
  }, [options, searchTerm]);

  const handleSelect = (option: EntityOption) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm('');
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    switch (focus) {
      case 'client': return 'Search for a client...';
      case 'artist': return 'Search for an artist...';
      case 'release': return 'Search for a release...';
      case 'campaign': return 'Search for a campaign...';
      default: return 'Search...';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Select {focus.charAt(0).toUpperCase() + focus.slice(1)}
      </label>

      <div className="relative">
        {/* Selected Entity Chip */}
        {selected && !isOpen ? (
          <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {selected.name}
              </div>
              {selected.subtitle && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selected.subtitle}
                </div>
              )}
            </div>
            <button
              onClick={handleClear}
              className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded transition-colors"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                placeholder={getPlaceholder()}
                disabled={loading}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                  placeholder-gray-400 dark:placeholder-gray-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Dropdown */}
            {isOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsOpen(false)}
                />
                
                {/* Options List */}
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {loading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      Loading...
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      No results found for "{searchTerm}"
                    </div>
                  ) : (
                    filteredOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleSelect(option)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 
                          border-b border-gray-100 dark:border-gray-700 last:border-0
                          transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {option.name}
                        </div>
                        {option.subtitle && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {option.subtitle}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
